import EmptyWorkspace from '@/components/layout/EmptyWorkspace';
import Balances from '@/components/modules/Balances';
import MarketList from '@/components/modules/MarketList';
import { useAppSelector } from '@/state/hooks';
import {
    selectSelectedXplorSpotWorkspaceTab,
    selectXplorSpotWorkspaceTabs,
    XplorSpotWorkspace
} from '@/state/reducers/workspaceSlice';
import { useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import {
    MdOutlineAccountBalanceWallet,
    MdOutlineCandlestickChart,
    MdOutlineHistory,
    MdOutlinePivotTableChart,
    MdOutlinePriceChange,
    MdOutlineSwapVerticalCircle
} from 'react-icons/md';
import { MosaicNode } from 'react-mosaic-component';
import { v4 as uuidv4 } from 'uuid';
import useLanguage from './useLanguage';

export type XplorSpotModules = 'Init' | 'Order' | 'Balances' | 'Chart' | 'Order Book' | 'Blotter' | 'Markets';
export const tabList = ['Order', 'Balances', 'Chart', 'Order Book', 'Blotter', 'Markets'];
export const tabIcon = {
    Order: MdOutlineSwapVerticalCircle,
    Balances: MdOutlineAccountBalanceWallet,
    Chart: MdOutlineCandlestickChart,
    'Order Book': MdOutlinePriceChange,
    Blotter: MdOutlineHistory,
    Markets: MdOutlinePivotTableChart
} as Record<XplorSpotModules, IconType>;

export const getDesktopLayout = (): MosaicNode<string> => {
    return {
        direction: 'row',
        first: {
            direction: 'row',
            first: {
                direction: 'column',
                first: `Order-${uuidv4()}`,
                second: `Balances-${uuidv4()}`,
                splitPercentage: 60
            },
            second: {
                direction: 'column',
                first: {
                    direction: 'row',
                    first: `Order Book-${uuidv4()}`,
                    second: `Chart-${uuidv4()}`,
                    splitPercentage: 40
                },
                second: `Blotter-${uuidv4()}`,
                splitPercentage: 60
            },
            splitPercentage: 30
        },
        second: `Markets-${uuidv4()}`,
        splitPercentage: 75
    };
};

export const getMobileLayout = (): MosaicNode<string> => {
    return {
        direction: 'row',
        first: `Order`,
        second: ``,
        splitPercentage: 100
    };
};

export const defaultXplorSpotLayout = () => {
    return {
        mobile: getMobileLayout(),
        desktop: getDesktopLayout()
    };
};

export type ConfigType = Record<
    string,
    {
        key: string;
        title: string;
        component?: JSX.Element;
    }
>;

export interface UseXplorSpotLayoutReturn {
    workspace: XplorSpotWorkspace;
    config: ConfigType;
}

const useXplorSpotLayout = (): UseXplorSpotLayoutReturn => {
    const lang = useLanguage();

    const workspaceTab = useAppSelector(selectSelectedXplorSpotWorkspaceTab);
    const workspaceTabs = useAppSelector(selectXplorSpotWorkspaceTabs);

    const workspace = useMemo(() => workspaceTab || workspaceTabs[0], [workspaceTab, workspaceTabs]);

    const config: ConfigType = useMemo(() => {
        return {
            Init: {
                key: 'Init',
                title: '',
                component: <EmptyWorkspace tabList={tabList} tabIcon={tabIcon} platform="xplorspot" />
            },
            Order: {
                key: 'Order',
                title: lang.modules.order
            },
            Balances: {
                key: 'Balances',
                title: lang.modules.balances,
                component: (
                    <div id="balances" className="h-full bg-brand-background">
                        <Balances />
                    </div>
                )
            },
            Chart: {
                key: 'Chart',
                title: lang.modules.chart
            },
            'Order Book': {
                key: 'Order Book',
                title: lang.modules['order-book']
            },
            Blotter: {
                key: 'Blotter',
                title: lang.modules.blotter
            },
            Markets: {
                key: 'Markets',
                title: lang.modules.markets,
                component: (
                    <div id="market-list" className="bg-brand-background h-full max-h-full overflow-hidden">
                        <MarketList />
                    </div>
                )
            }
        };
    }, [lang]);

    return { workspace, config };
};

export default useXplorSpotLayout;
